import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Badge,
  Tooltip,
  Avatar,
  Divider,
  Collapse,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Inventory2 as InventorySettingsIcon,
  ShoppingCart as ShoppingCartIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  AccountBalanceWallet as WalletIcon,
  ListAlt as ListAltIcon,
  PointOfSale as CashierIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ArrowBack as ArrowBackIcon,
  BusinessCenter as CompanySettingsIcon,
  Percent as VatIcon,
  CurrencyExchange as CurrencyIcon,
  Policy as PolicyIcon,
  Email as EmailIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useCartData } from '../context/CartContext';
import { useOpenOrdersCount } from '../context/OpenOrdersCountContext';
import { ROLES } from '../constants/roles';
import { hasPermission } from '../utils/permissions';
import { buildInventorySubItems, isInventorySubItemSelected } from '../utils/inventoryRoutes';
import {
  CLIENT_MANAGEMENT_SUB_ITEMS,
  getClientManagementPath,
  isClientManagementRoute,
  isClientManagementTabSelected,
} from '../utils/settingsForm';

const DRAWER_EXPANDED = 240;
const DRAWER_MINI = 64;

// ── NavItem ───────────────────────────────────────────────────────────────────
// Defined outside Layout so React never remounts it when Layout re-renders
// (e.g. badge count changes). React.memo bails out for items whose props are stable.
const NavItem = React.memo(function NavItem({
  navKey,
  icon,
  path,
  section,
  labelKey,
  expanded,
  isRtl,
  isSettingsRoute,
  settingsSection,
  locationPathname,
  setMobileOpen,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const label = labelKey ? t(labelKey) : t(`nav.${navKey}`);
  const selected = isSettingsRoute && section
    ? locationPathname.startsWith('/settings') && settingsSection === section
    : locationPathname === path || locationPathname.startsWith(`${path}/`);

  const button = (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <ListItemButton
        selected={selected}
        onClick={() => {
          navigate(path);
          if (isMobile) setMobileOpen(false);
        }}
        sx={{
          minHeight: 48,
          justifyContent: expanded ? 'initial' : 'center',
          px: 2.5,
          borderRadius: 1,
          mx: 0.5,
          '&.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
            '&:hover': { bgcolor: 'primary.dark' },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: expanded ? 2 : 'auto',
            justifyContent: 'center',
            color: selected ? 'inherit' : 'text.secondary',
          }}
        >
          {icon}
        </ListItemIcon>
        {expanded && <ListItemText primary={label} />}
      </ListItemButton>
    </ListItem>
  );

  if (!expanded) {
    return <Tooltip title={label} placement={isRtl ? 'left' : 'right'}>{button}</Tooltip>;
  }
  return button;
});

// ── InventoryNavGroup ─────────────────────────────────────────────────────────
// Defined outside Layout so React never recreates it on parent re-renders.
const InventoryNavGroup = React.memo(function InventoryNavGroup({
  expanded,
  isRtl,
  isInventoryActive,
  inventoryOpen,
  setInventoryOpen,
  inventoryMenuAnchor,
  setInventoryMenuAnchor,
  inventorySubItems,
  navigateInventory,
  locationPathname,
}) {
  const { t } = useTranslation();
  const parentLabel = t('nav.inventory');

  const subItemButtonSx = {
    minHeight: 36,
    pl: expanded ? 4.5 : 2.5,
    pr: 2,
    py: 0.5,
    borderRadius: 1,
    mx: 0.5,
    color: 'text.primary',
    '&.Mui-selected': {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      fontWeight: 600,
      '&:hover': { bgcolor: 'primary.dark' },
    },
  };

  const parentButtonSx = {
    minHeight: 48,
    px: 2.5,
    borderRadius: 1,
    mx: 0.5,
    color: isInventoryActive ? 'primary.main' : 'text.primary',
    '& .MuiListItemIcon-root': {
      color: isInventoryActive ? 'primary.main' : 'text.secondary',
    },
    '&:hover': {
      bgcolor: 'action.hover',
    },
  };

  if (!expanded) {
    return (
      <>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title={parentLabel} placement={isRtl ? 'left' : 'right'}>
            <ListItemButton
              onClick={(e) => setInventoryMenuAnchor(e.currentTarget)}
              sx={{ ...parentButtonSx, justifyContent: 'center' }}
            >
              <ListItemIcon
                sx={{ minWidth: 0, justifyContent: 'center', color: isInventoryActive ? 'inherit' : 'text.secondary' }}
              >
                <InventoryIcon />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>
        <Menu
          anchorEl={inventoryMenuAnchor}
          open={Boolean(inventoryMenuAnchor)}
          onClose={() => setInventoryMenuAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'right' : 'left' }}
        >
          {inventorySubItems.map((item) => (
            <MenuItem
              key={item.key}
              selected={isInventorySubItemSelected(locationPathname, item.path)}
              onClick={() => navigateInventory(item.path)}
            >
              {t(item.labelKey)}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          onClick={() => setInventoryOpen((open) => !open)}
          sx={{ ...parentButtonSx, justifyContent: 'initial' }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: 'center' }}>
            <InventoryIcon />
          </ListItemIcon>
          <ListItemText
            primary={parentLabel}
            primaryTypographyProps={{ fontWeight: isInventoryActive ? 600 : 400 }}
          />
          {inventoryOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </ListItemButton>
      </ListItem>
      <Collapse in={inventoryOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pb: 0.5 }}>
          {inventorySubItems.map((item) => {
            const selected = isInventorySubItemSelected(locationPathname, item.path);
            return (
              <ListItem key={item.key} disablePadding sx={{ display: 'block' }}>
                <ListItemButton selected={selected} onClick={() => navigateInventory(item.path)} sx={subItemButtonSx}>
                  <ListItemText primary={t(item.labelKey)} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </>
  );
});

// ── ClientManagementNavGroup ──────────────────────────────────────────────────
const ClientManagementNavGroup = React.memo(function ClientManagementNavGroup({
  expanded,
  isRtl,
  isClientManagementActive,
  clientManagementOpen,
  setClientManagementOpen,
  clientManagementMenuAnchor,
  setClientManagementMenuAnchor,
  navigateClientManagement,
  locationSearch,
}) {
  const { t } = useTranslation();
  const parentLabel = t('settings.sections.clientManagement');

  const subItemButtonSx = {
    minHeight: 36,
    pl: expanded ? 4.5 : 2.5,
    pr: 2,
    py: 0.5,
    borderRadius: 1,
    mx: 0.5,
    color: 'text.primary',
    '&.Mui-selected': {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      fontWeight: 600,
      '&:hover': { bgcolor: 'primary.dark' },
    },
  };

  const parentButtonSx = {
    minHeight: 48,
    px: 2.5,
    borderRadius: 1,
    mx: 0.5,
    color: isClientManagementActive ? 'primary.main' : 'text.primary',
    '& .MuiListItemIcon-root': {
      color: isClientManagementActive ? 'primary.main' : 'text.secondary',
    },
    '&:hover': {
      bgcolor: 'action.hover',
    },
  };

  if (!expanded) {
    return (
      <>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title={parentLabel} placement={isRtl ? 'left' : 'right'}>
            <ListItemButton
              onClick={(e) => setClientManagementMenuAnchor(e.currentTarget)}
              sx={{ ...parentButtonSx, justifyContent: 'center' }}
            >
              <ListItemIcon
                sx={{ minWidth: 0, justifyContent: 'center', color: isClientManagementActive ? 'inherit' : 'text.secondary' }}
              >
                <PolicyIcon />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>
        <Menu
          anchorEl={clientManagementMenuAnchor}
          open={Boolean(clientManagementMenuAnchor)}
          onClose={() => setClientManagementMenuAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'right' : 'left' }}
        >
          {CLIENT_MANAGEMENT_SUB_ITEMS.map((item) => (
            <MenuItem
              key={item.key}
              selected={isClientManagementTabSelected(locationSearch, item.tab)}
              onClick={() => navigateClientManagement(item.tab)}
            >
              {t(item.labelKey)}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          onClick={() => setClientManagementOpen((open) => !open)}
          sx={{ ...parentButtonSx, justifyContent: 'initial' }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: 'center' }}>
            <PolicyIcon />
          </ListItemIcon>
          <ListItemText
            primary={parentLabel}
            primaryTypographyProps={{ fontWeight: isClientManagementActive ? 600 : 400 }}
          />
          {clientManagementOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </ListItemButton>
      </ListItem>
      <Collapse in={clientManagementOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pb: 0.5 }}>
          {CLIENT_MANAGEMENT_SUB_ITEMS.map((item) => {
            const selected = isClientManagementTabSelected(locationSearch, item.tab);
            return (
              <ListItem key={item.key} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  selected={selected}
                  onClick={() => navigateClientManagement(item.tab)}
                  sx={subItemButtonSx}
                >
                  <ListItemText primary={t(item.labelKey)} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </>
  );
});

/** Persists across navigations — each page mounts its own Layout. */
const NAV_DRAWER_EXPANDED_KEY = 'layout_nav_drawer_expanded';

function readStoredDrawerExpanded() {
  try {
    const raw = localStorage.getItem(NAV_DRAWER_EXPANDED_KEY);
    if (raw === null) return true;
    return raw === 'true';
  } catch {
    return true;
  }
}

const Layout = ({ children, settingsMode = false }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => readStoredDrawerExpanded());

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isRtl = theme.direction === 'rtl';

  const { user, activeCompany, isInternal, logout, userRole, customPermissions } = useAuth();
  const { cartCount } = useCartData();
  const { openOrdersMenuCount } = useOpenOrdersCount();
  const { t } = useTranslation();

  const drawerWidth = expanded ? DRAWER_EXPANDED : DRAWER_MINI;

  const handleDrawerToggle = () => setMobileOpen((v) => !v);
  const handleExpandToggle = () => {
    setExpanded((v) => {
      const next = !v;
      try {
        localStorage.setItem(NAV_DRAWER_EXPANDED_KEY, String(next));
      } catch {
        /* ignore quota / private mode */
      }
      return next;
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials =
    `${user?.firstName?.charAt(0) ?? ''}${user?.lastName?.charAt(0) ?? ''}`.toUpperCase();

  const canSeeClients =
    isInternal && hasPermission(userRole, 'manage_clients', user?.customPermissions);
  const canSeeCashiering =
    isInternal && (
      hasPermission(userRole, 'manage_checks', customPermissions)
      || hasPermission(userRole, 'remove_check_item', customPermissions)
    );
  const canSeeUsers = isInternal && userRole === ROLES.MANAGER;
  const canSeeInventoryRequirements = isInternal && (
    userRole === ROLES.MANAGER
    || hasPermission(userRole, 'manage_inventory_requirements', customPermissions)
  );
  const canSeeAuditLogs = isInternal && (
    userRole === ROLES.MANAGER || hasPermission(userRole, 'view_audit_logs', customPermissions)
  );
  const isManager = userRole === ROLES.MANAGER;
  const isSettingsRoute = settingsMode || location.pathname.startsWith('/settings');
  const settingsSection = new URLSearchParams(location.search).get('section') || 'profile';

  // Shared props passed to every NavItem; memoized so React.memo can bail out
  // when only unrelated Layout state (e.g. inventory submenu open state) changes.
  const navItemSharedProps = useMemo(() => ({
    expanded,
    isRtl,
    isSettingsRoute,
    settingsSection,
    locationPathname: location.pathname,
    setMobileOpen,
  }), [expanded, isRtl, isSettingsRoute, settingsSection, location.pathname]);

  const inventorySubItems = useMemo(
    () => buildInventorySubItems(canSeeInventoryRequirements),
    [canSeeInventoryRequirements]
  );

  const [inventoryOpen, setInventoryOpen] = useState(
    () => location.pathname.startsWith('/inventory')
  );
  const [inventoryMenuAnchor, setInventoryMenuAnchor] = useState(null);

  useEffect(() => {
    if (location.pathname.startsWith('/inventory')) {
      setInventoryOpen(true);
    }
  }, [location.pathname]);

  const mainNavItemsAfterInventory = [
    {
      key: 'cart',
      icon: (
        <Badge badgeContent={cartCount} color="error" max={99}>
          <ShoppingCartIcon />
        </Badge>
      ),
      path: '/cart',
    },
    {
      key: 'orders',
      icon: (
        <Badge badgeContent={openOrdersMenuCount} color="error" max={99} invisible={openOrdersMenuCount === 0}>
          <ReceiptIcon />
        </Badge>
      ),
      path: '/orders',
    },
    ...(!isInternal ? [
      { key: 'specialRequests', icon: <ListAltIcon />, path: '/special-requests' },
      { key: 'myAccount', icon: <WalletIcon />, path: '/my-account' },
    ] : []),
    ...(canSeeCashiering ? [{ key: 'cashiering', icon: <CashierIcon />, path: '/cashiering' }] : []),
    ...(canSeeClients ? [{ key: 'clients', icon: <BusinessIcon />, path: '/clients' }] : []),
    ...(canSeeAuditLogs ? [{ key: 'auditLogs', icon: <HistoryIcon />, path: '/audit-logs' }] : []),
    ...(canSeeUsers ? [{ key: 'users', icon: <PeopleIcon />, path: '/users' }] : []),
  ];

  const settingsNavItems = [
    { key: 'settingsProfile', icon: <PersonIcon />, path: '/settings?section=profile', section: 'profile', labelKey: 'settings.profile' },
    ...(isManager ? [
      { key: 'settingsCompany', icon: <CompanySettingsIcon />, path: '/settings?section=company', section: 'company', labelKey: 'settings.sections.company' },
      { key: 'settingsVat', icon: <VatIcon />, path: '/settings?section=vat', section: 'vat', labelKey: 'settings.sections.vat' },
      { key: 'settingsCurrency', icon: <CurrencyIcon />, path: '/settings?section=currency', section: 'currency', labelKey: 'settings.sections.currency' },
      { key: 'settingsInventory', icon: <InventorySettingsIcon />, path: '/settings?section=inventory&tab=lots', section: 'inventory', labelKey: 'settings.sections.inventory' },
      { key: 'settingsSpecialRequests', icon: <ListAltIcon />, path: '/settings?section=specialRequests', section: 'specialRequests', labelKey: 'settings.sections.specialRequests' },
      { key: 'settingsClientManagement', type: 'clientManagementGroup' },
      { key: 'settingsEmailNotifications', icon: <EmailIcon />, path: '/settings?section=emailNotifications', section: 'emailNotifications', labelKey: 'settings.sections.emailNotifications' },
    ] : []),
  ];

  const isInventoryActive = location.pathname.startsWith('/inventory');
  const isClientManagementActive = isClientManagementRoute(location.search);

  const [clientManagementOpen, setClientManagementOpen] = useState(
    () => isClientManagementRoute(location.search),
  );
  const [clientManagementMenuAnchor, setClientManagementMenuAnchor] = useState(null);

  useEffect(() => {
    if (isClientManagementRoute(location.search)) {
      setClientManagementOpen(true);
    }
  }, [location.search]);

  const navigateClientManagement = useCallback((tab) => {
    navigate(getClientManagementPath(tab));
    setClientManagementMenuAnchor(null);
    if (isMobile) setMobileOpen(false);
  }, [navigate, isMobile]);

  const navigateInventory = useCallback((path) => {
    navigate(path);
    setInventoryMenuAnchor(null);
    if (isMobile) setMobileOpen(false);
  }, [navigate, isMobile]);

  const drawerContent = (isDesktop = false) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Brand / user section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: expanded ? 2 : 1,
          py: 1.5,
          gap: 1.5,
          minHeight: 64,
          flexShrink: 0,
        }}
      >
        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: 14, flexShrink: 0 }}>
          {initials || '?'}
        </Avatar>
        {expanded && (
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {activeCompany?.companyName}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider />

      {/* Main nav */}
      <List sx={{ flexGrow: 1, py: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {isSettingsRoute && expanded && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {t('settings.title')}
            </Typography>
          </Box>
        )}
        {isSettingsRoute ? (
          settingsNavItems.map((item) => (
            item.type === 'clientManagementGroup' ? (
              <ClientManagementNavGroup
                key={item.key}
                expanded={expanded}
                isRtl={isRtl}
                isClientManagementActive={isClientManagementActive}
                clientManagementOpen={clientManagementOpen}
                setClientManagementOpen={setClientManagementOpen}
                clientManagementMenuAnchor={clientManagementMenuAnchor}
                setClientManagementMenuAnchor={setClientManagementMenuAnchor}
                navigateClientManagement={navigateClientManagement}
                locationSearch={location.search}
              />
            ) : (
              <NavItem
                key={item.key}
                navKey={item.key}
                icon={item.icon}
                path={item.path}
                section={item.section}
                labelKey={item.labelKey}
                {...navItemSharedProps}
              />
            )
          ))
        ) : (
          <>
            {isInternal && (
              <NavItem key="dashboard" navKey="dashboard" icon={<DashboardIcon />} path="/dashboard" {...navItemSharedProps} />
            )}
            <InventoryNavGroup
              expanded={expanded}
              isRtl={isRtl}
              isInventoryActive={isInventoryActive}
              inventoryOpen={inventoryOpen}
              setInventoryOpen={setInventoryOpen}
              inventoryMenuAnchor={inventoryMenuAnchor}
              setInventoryMenuAnchor={setInventoryMenuAnchor}
              inventorySubItems={inventorySubItems}
              navigateInventory={navigateInventory}
              locationPathname={location.pathname}
            />
            {mainNavItemsAfterInventory.map((item) => (
              <NavItem
                key={item.key}
                navKey={item.key}
                icon={item.icon}
                path={item.path}
                section={item.section}
                labelKey={item.labelKey}
                {...navItemSharedProps}
              />
            ))}
          </>
        )}
      </List>

      <Divider />

      {/* Bottom: Settings / Back + Logout */}
      <List sx={{ py: 1, flexShrink: 0 }}>
        {isSettingsRoute ? (
          <NavItem key="backToApp" navKey="backToApp" icon={<ArrowBackIcon />} path="/dashboard" labelKey="settings.backToApp" {...navItemSharedProps} />
        ) : (
          <NavItem key="settings" navKey="settings" icon={<SettingsIcon />} path="/settings" {...navItemSharedProps} />
        )}

        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip
            title={!expanded ? t('nav.logout') : ''}
            placement={isRtl ? 'left' : 'right'}
          >
            <ListItemButton
              onClick={handleLogout}
              sx={{
                minHeight: 48,
                justifyContent: expanded ? 'initial' : 'center',
                px: 2.5,
                borderRadius: 1,
                mx: 0.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: expanded ? 2 : 'auto',
                  justifyContent: 'center',
                  color: 'error.main',
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              {expanded && (
                <ListItemText
                  primary={t('nav.logout')}
                  primaryTypographyProps={{ color: 'error.main' }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>

      {/* Toggle button (desktop only) */}
      {isDesktop && (
        <>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: expanded ? 'flex-end' : 'center', p: 0.5 }}>
            <IconButton onClick={handleExpandToggle} size="small">
              {expanded
                ? isRtl ? <ChevronRightIcon /> : <ChevronLeftIcon />
                : isRtl ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Mobile AppBar */}
      <AppBar
        position="fixed"
        sx={{
          display: { md: 'none' },
          top: 0,
        }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontSize: '0.95rem' }}>
            {activeCompany?.companyName || 'Pich Jewels'}
          </Typography>
          <IconButton color="inherit" onClick={() => navigate('/cart')}>
            <Badge badgeContent={cartCount} color="error" max={99}>
              <ShoppingCartIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Nav */}
      <Box
        component="nav"
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* Mobile overlay drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_EXPANDED,
            },
          }}
        >
          {drawerContent(false)}
        </Drawer>

        {/* Desktop permanent drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {drawerContent(true)}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: '56px', md: 0 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          minHeight: '100vh',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
